import { FC, useMemo } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { useRequestErrorsInForm } from 'hooks/useErrors'
import { yupResolver } from '@hookform/resolvers/yup'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { CreateCCPaymentMethodAction, ResetCreateCCPaymentMethodAction } from 'components/features/creditCard/creditCardStore/actions'
import { createPaymentMethodRequestSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { createCardPaymentMethodSchema } from 'components/features/creditCard/creditCardStore/schemas'
import { CCCreatePaymentMethodApiRequest } from 'components/features/creditCard/creditCardStore/types'
import { LabeledInput } from 'components/atoms/inputs/LabeledInput/LabeledInput'
import { FormPreset } from 'components/atoms/inputs/FormPreset/FormPreset'
import Button from 'components/features/pfm/Button/Button'

type AddPaymentMethodFormProps = {
  formContext: UseFormReturn<CCCreatePaymentMethodApiRequest>
  // Add this on will make sure the user returns into the payment flow
  // if that is how they got to this form
  paymentMode?: 'payCard' | 'autoPay'
  onDismissErrors?: () => void
}

export function usePaymentMethodForm (): UseFormReturn<CCCreatePaymentMethodApiRequest> {
  return useForm<CCCreatePaymentMethodApiRequest>({
    reValidateMode: 'onChange',
    resolver: yupResolver(createCardPaymentMethodSchema),
    defaultValues: {
      cardholder_name: '',
      routing_number: '',
      account_number: '',
      confirm_account_number: '',
      nickname: undefined,
      account_type: 'checking',
    },
  })
}

const AddPaymentMethodForm: FC<AddPaymentMethodFormProps> = (props) => {
  const { formContext, paymentMode } = props
  const { handleSubmit, setError, clearErrors } = formContext
  const { isLoading } = useStoreSelector(createPaymentMethodRequestSelector)
  const createMethodrequest = useStoreSelector(createPaymentMethodRequestSelector)
  useRequestErrorsInForm(createMethodrequest, setError)

  const dispatch = useStoreDispatch()
  const handleSubmitForm = useMemo(() => handleSubmit((formValues) => {
    dispatch(new ResetCreateCCPaymentMethodAction())
    dispatch(new CreateCCPaymentMethodAction({
      paymentMethod: formValues,
      paymentMode,
    }))
    clearErrors()
  }), [clearErrors, dispatch, handleSubmit, paymentMode])

  return (
    <FormPreset formContext={ formContext } onSubmit={ handleSubmitForm }>
      <LabeledInput<'checking' | 'savings'>
        label='Account Type'
        name='account_type'
        type='radio'
        horizontal
        style='bare'
        options={ [
          { label: 'Checking', value: 'checking' },
          { label: 'Savings', value: 'savings' },
        ] }
        disabled={ isLoading }
      />
      <LabeledInput
        label='Full Name of Account Holder'
        name='cardholder_name'
        placeholder='John Doe'
        maxLength={ 75 }
        disabled={ isLoading }
      />
      <LabeledInput
        label='Routing Number'
        name='routing_number'
        placeholder='123456789'
        type='routing'
        disabled={ isLoading }
      />
      <LabeledInput
        label='Account Number'
        name='account_number'
        type='bank_account'
        disabled={ isLoading }
      />
      <LabeledInput
        label='Confirm Account Number'
        name='confirm_account_number'
        type='bank_account'
        disabled={ isLoading }
      />
      <LabeledInput
        label='Account Nickname'
        name='nickname'
        note='This field is optional'
        maxLength={ 35 }
        disabled={ isLoading }
      />
      <Button text='Save Account' submit isSubmitting={ createMethodrequest?.isLoading } />
    </FormPreset>
  )
}

export default AddPaymentMethodForm
